import { useEffect, useState } from "react";
import { api, apiError, friendlyError, logIssue } from "@/lib/api";
import { MediaField } from "@/components/admin/Media";
import { Loader2, Plus, Trash2, Save } from "lucide-react";

export const Field = ({ label, value, onChange, type = "text", rows, options }) => (
  <label className="block">
    <span className="ap-label">{label}</span>
    {options ? (
      <select value={value} onChange={(e) => onChange(e.target.value)} className="ap-field">
        {options.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    ) : rows ? (
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="ap-field resize-y" />
    ) : type === "checkbox" ? (
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 accent-signal" />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
        className="ap-field"
      />
    )}
  </label>
);

export const Toast = ({ msg }) =>
  msg ? (
    <p data-testid="admin-toast" className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 border border-ink bg-ink px-5 py-3 font-mono text-xs uppercase tracking-widest text-paper">
      {msg}
    </p>
  ) : null;

/** Generic list editor for works / motifs / faqs / products */
export function CollectionEditor({ endpoint, title, fields, blank, testid }) {
  const [items, setItems] = useState(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/${endpoint}`);
      setItems(data);
      setError("");
    } catch (e) {
      logIssue(`GET /admin/${endpoint}`, e);
      setItems([]);
      setError(friendlyError(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 2200);
  };

  const update = (id, key, value) =>
    setItems((list) => list.map((it) => (it.id === id ? { ...it, [key]: value } : it)));

  const save = async (item) => {
    setError("");
    try {
      await api.put(`/admin/${endpoint}/${item.id}`, item);
      flash("Gespeichert");
    } catch (e) {
      setError(apiError(e.response?.data?.detail));
    }
  };

  const create = async () => {
    setError("");
    try {
      const { data } = await api.post(`/admin/${endpoint}`, blank);
      setItems((l) => [...l, data]);
      flash("Eintrag erstellt");
    } catch (e) {
      setError(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/${endpoint}/${id}`);
      setItems((l) => l.filter((i) => i.id !== id));
      flash("Gelöscht");
    } catch (e) {
      logIssue(`DELETE /admin/${endpoint}`, e);
      setError(friendlyError(e));
    }
  };

  if (!items) return <Loader2 className="animate-spin text-charcoal" />;

  return (
    <div data-testid={testid}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="ap-h3 text-ink">{title}</h2>
        <button type="button" onClick={create} className="ap-btn-solid" data-testid={`${testid}-add`}>
          <Plus size={15} /> Neu
        </button>
      </div>
      {error ? <p className="mb-4 border border-destructive p-3 text-sm text-destructive">{error}</p> : null}

      <div className="space-y-5">
        {items.map((item, idx) => (
          <div key={item.id} data-testid={`${testid}-item-${idx}`} className="border border-paper-line bg-paper p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
                  {f.type === "media" ? (
                    <MediaField
                      label={f.label}
                      value={item[f.key] ?? ""}
                      onChange={(v) => update(item.id, f.key, v)}
                    />
                  ) : (
                    <Field
                      label={f.label}
                      type={f.type}
                      rows={f.rows}
                      options={f.options}
                      value={item[f.key] ?? ""}
                      onChange={(v) => update(item.id, f.key, v)}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={() => save(item)} className="ap-btn-solid" data-testid={`${testid}-save-${idx}`}>
                <Save size={15} /> Speichern
              </button>
              <button type="button" onClick={() => remove(item.id)} className="ap-btn-ghost" data-testid={`${testid}-delete-${idx}`}>
                <Trash2 size={15} /> Löschen
              </button>
            </div>
          </div>
        ))}
      </div>
      <Toast msg={msg} />
    </div>
  );
}
